import React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useContractAdjustmentWizard } from "./useContractAdjustmentWizard";
import { ContractAdjustmentDataStep } from "./ContractAdjustmentDataStep";
import { ContractAdjustmentEvidenceStep } from "./ContractAdjustmentEvidenceStep";
import { ContractAdjustmentReviewStep } from "./ContractAdjustmentReviewStep";
import type { ContractAdjustmentView } from "./contractAdjustmentsUtils";

export type ContractAdjustmentWizardProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  installationId: number;
  existingAdjustment?: ContractAdjustmentView | null;
  onSuccess: () => void;
  adjustmentsRefetch?: () => void;
  ceilingRefetch?: () => void;
};

export function ContractAdjustmentWizard(props: ContractAdjustmentWizardProps) {
  const { open, onOpenChange, installationId, existingAdjustment, onSuccess, adjustmentsRefetch: adjRef, ceilingRefetch: ceilRef } = props;

  const ctrl = useContractAdjustmentWizard({
    installationId, existingAdjustment, open, onOpenChange, onSuccess,
    adjustmentsRefetch: adjRef || (() => {}), ceilingRefetch: ceilRef || (() => {}),
  });

  const stepLabels: Record<number, string> = { 1: "Datos", 2: "Evidencias", 3: "Revisión" };

  return (
    <>
      {/* Main dialog */}
      <Dialog open={open && !ctrl.showCloseConfirm} onOpenChange={ctrl.handleClose}>
        <DialogContent className="max-h-[90dvh] w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {ctrl.isPendingEvidenceMode ? "Gestionar evidencias" : ctrl.isEdit ? "Editar modificación contractual" : "Añadir ampliación o anexo"}
            </DialogTitle>
            <DialogDescription>
              {ctrl.isPendingEvidenceMode ? "Gestión de evidencias" : `Paso ${ctrl.step} de 3`}
            </DialogDescription>
          </DialogHeader>

          {/* Step indicators */}
          {!ctrl.isPendingEvidenceMode && (
            <div className="flex items-center gap-2">
              {([1, 2, 3] as const).map(s => (
                <div key={s} className="flex items-center gap-1.5">
                  <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${s === ctrl.step ? "bg-primary text-primary-foreground" : s < ctrl.step ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground"}`}>
                    {s < ctrl.step ? "✓" : s}
                  </div>
                  <span className={`hidden text-xs sm:inline ${s === ctrl.step ? "font-medium" : "text-muted-foreground"}`}>{stepLabels[s]}</span>
                </div>
              ))}
            </div>
          )}

          {/* Step 1 */}
          {!ctrl.isPendingEvidenceMode && ctrl.step === 1 && (
            <ContractAdjustmentDataStep formData={ctrl.formData} errors={ctrl.errors} handleInputChange={ctrl.handleInputChange} handleAdjustmentTypeChange={ctrl.handleAdjustmentTypeChange} />
          )}

          {/* Step 2 / pending evidence */}
          {(ctrl.step === 2 || ctrl.isPendingEvidenceMode) && (
            <div className="py-2">
              {ctrl.isPendingEvidenceMode && (
                <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">Solo puedes gestionar evidencias. Los datos económicos y descriptivos no se pueden modificar.</div>
              )}
              <ContractAdjustmentEvidenceStep
                availableDocuments={ctrl.evidence.availableDocuments}
                attachedEvidence={ctrl.evidence.attachedEvidence}
                attachedDocIds={ctrl.evidence.attachedDocIds}
                isLoadingEvidence={ctrl.evidence.isLoadingEvidence}
                isUploading={ctrl.evidence.isUploading}
                canSubmitEvidence={ctrl.evidence.canSubmitEvidence}
                attachExisting={ctrl.evidence.attachExisting}
                detachEvidence={ctrl.evidence.detachEvidence}
                updateEvidenceType={ctrl.evidence.updateEvidenceType}
                uploadAndAttach={ctrl.evidence.uploadAndAttach}
                isAnyPending={ctrl.isAnyPending}
              />
            </div>
          )}

          {/* Step 3 */}
          {!ctrl.isPendingEvidenceMode && ctrl.step === 3 && (
            <ContractAdjustmentReviewStep
              formData={ctrl.formData}
              ceiling={ctrl.ceiling}
              attachedEvidence={ctrl.evidence.attachedEvidence}
              isSubmitting={ctrl.isSubmitting}
              canSubmit={ctrl.canSubmit}
              isEdit={ctrl.isEdit}
              readEvidenceFromBackend={ctrl.evidence.readEvidenceFromBackend}
            />
          )}

          {/* Footer */}
          <DialogFooter className="gap-2">
            {ctrl.isPendingEvidenceMode ? (
              <Button variant="outline" onClick={ctrl.handleClose}>Cerrar</Button>
            ) : ctrl.step === 1 ? (
              <>
                <Button variant="outline" onClick={ctrl.handleClose} disabled={ctrl.isAnyPending}>Cancelar</Button>
                <Button onClick={ctrl.goNext} disabled={ctrl.isAnyPending}>
                  {ctrl.isCreatingDraft ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : "Siguiente"}
                </Button>
              </>
            ) : ctrl.step === 2 ? (
              <>
                <Button variant="outline" onClick={ctrl.goBack} disabled={ctrl.isAnyPending}>Anterior</Button>
                <Button variant="secondary" onClick={ctrl.handleSaveDraft} disabled={ctrl.isAnyPending}>Guardar borrador</Button>
                <Button onClick={ctrl.goNext} disabled={ctrl.isAnyPending}>Siguiente</Button>
              </>
            ) : (
              /* Step 3: Guardar borrador + Enviar */
              <>
                <Button variant="outline" onClick={ctrl.goBack} disabled={ctrl.isAnyPending}>Anterior</Button>
                <Button variant="secondary" onClick={ctrl.handleSaveDraft} disabled={ctrl.isAnyPending}>Guardar borrador</Button>
                <Button onClick={ctrl.handleSubmit} disabled={!ctrl.canSubmit || ctrl.isAnyPending}>
                  {ctrl.isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</> : "Enviar para aprobación"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close confirmation dialog */}
      <Dialog open={ctrl.showCloseConfirm} onOpenChange={ctrl.cancelClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {ctrl.activeAdjustmentId && !ctrl.isPendingEvidenceMode ? "Borrador guardado" : ctrl.isPendingEvidenceMode ? "Evidencias guardadas" : "¿Descartar cambios?"}
            </DialogTitle>
            <DialogDescription>{ctrl.closeConfirmMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={ctrl.cancelClose}>
              {ctrl.activeAdjustmentId ? "Seguir editando" : "Seguir editando"}
            </Button>
            <Button
              variant={!ctrl.activeAdjustmentId && !ctrl.isPendingEvidenceMode ? "destructive" : "default"}
              onClick={ctrl.confirmClose}
            >
              {ctrl.activeAdjustmentId && !ctrl.isPendingEvidenceMode ? "Salir y conservar borrador" : "Cerrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}